import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    Input,
    input,
    OnInit,
    output,
    Signal,
    signal,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
    TelXLDropdownComponent,
    TelXLLoadingComponent,
    TelXLSearchComponent,
} from '@dxp/elements';
import {
    IContact,
    IContactDirectory,
    IContactDirectoryEvent,
    IContacts,
    IDirectoryListingEvent,
    IDirectoryListingExtended,
    IDirectorySelection,
    IDropdown,
} from '@dxp/core/models';
import { ContactDirectoryService, ContactService } from '@dxp/core/services';
import { debounceTime, filter, fromEvent, map, Observable, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ListingMode, MediaOptions, MediaType } from '@dxp/core/types';
import { TelXLDirectoryListingItemComponent } from '../item/directory-listing-item.component';

const LIMIT = 20;

@Component({
    selector: 'telxl-shared-contact-directory-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        TelXLLoadingComponent,
        TelXLDirectoryListingItemComponent,
        TelXLSearchComponent,
        TelXLDropdownComponent,
    ],
    templateUrl: './contact-directory-panel.component.html',
    styleUrl: './contact-directory-panel.component.scss',
})
export class TelXLContactDirectoryPanelComponent
    implements OnInit, AfterViewInit
{
    readonly #destroyRef = inject(DestroyRef);
    readonly #contactDirectoryService = inject(ContactDirectoryService);
    readonly #contactService = inject(ContactService);

    readonly #requiresEmail = signal<boolean>(false);
    readonly #requiresPhone = signal<boolean>(false);
    readonly #requiresName = signal<boolean>(false);
    readonly #media = signal<MediaOptions>('All');
    readonly #directory = signal<number>(0);

    @Input()
    set requiresEmail(value: boolean) {
        this.#requiresEmail.set(value);
    }

    @Input()
    set requiresPhone(value: boolean) {
        this.#requiresPhone.set(value);
    }

    @Input()
    set requiresName(value: boolean) {
        this.#requiresName.set(value);
    }

    @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

    customButtonTemplate = input<TemplateRef<any>>();
    selected = input<IDirectorySelection>();
    highlight = input<number>();
    height = input.required<string>();
    mode = input<ListingMode[]>([]);
    displayMediaIcons = input<MediaType[]>([]);
    showMediaFilter = input<boolean>(false);
    showDirectoryFilter = input<boolean>(false);
    disabled = input<boolean>(false);
    clicked = output<IContactDirectoryEvent>();

    readonly #contactDirectory = signal<IContacts>({
        total: 0,
        results: [],
    });

    readonly #contactDirectories: Signal<IContactDirectory[] | undefined>;
    readonly contactDirectories$: Observable<IContactDirectory[] | undefined>;

    readonly searchText = signal<string>('');
    readonly isLoading = signal<boolean>(false);

    readonly calculateHeight = computed<string>(
        () => `calc(${this.height()} - 100px)`,
    );

    mediaMenuItems: IDropdown<MediaOptions>[] = [
        {
            label: 'All Media',
            value: 'All',
            disabled: false,
        },
        {
            label: 'With Voice',
            value: 'Voice',
            disabled: false,
        },
        {
            label: 'With Email',
            value: 'Email',
            disabled: false,
        },
    ];

    directoryMenuItems = computed<IDropdown<number>[]>(() => {
        const items = (this.#contactDirectories() ?? [])
            .map(directory => {
                return {
                    label: directory.name,
                    value: directory.id,
                    disabled: false,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        return [
            {
                label: 'All Directories',
                value: 0,
                disabled: false,
            },
            ...items,
        ];
    });

    listings = computed<IDirectoryListingExtended[]>(() =>
        this.#contactDirectory().results.map(contact => {
            return {
                id: contact.id.toString(),
                name: contact.fullName,
                phoneNumber: contact.phoneNumber,
                email: contact.emailAddress,
                directory: (this.#contactDirectories() ?? []).find(
                    cd => cd.id === contact.directory,
                ) as IContactDirectory,
                object: JSON.stringify(contact),
            };
        }),
    );

    constructor() {
        this.#contactDirectories = toSignal(
            this.#contactDirectoryService.fetchContactDirectories(),
            { initialValue: undefined },
        );

        this.contactDirectories$ = toObservable(this.#contactDirectories);
    }

    ngOnInit() {
        this.contactDirectories$
            .pipe(
                tap(
                    _ => this.#loadContacts(true),
                    takeUntilDestroyed(this.#destroyRef),
                ),
            )
            .subscribe();

        /**
         * Refresh the contact page - Only if the event is for the contacts directory
         */
        this.#contactService.contactUpdated$
            .pipe(
                filter(contactUpdate => contactUpdate.type === 'contacts'),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe(contactUpdate =>
                this.refresh(contactUpdate.contact as IContact),
            );
    }

    ngAfterViewInit() {
        if (this.scrollContainer) {
            fromEvent(this.scrollContainer.nativeElement, 'scroll')
                .pipe(
                    debounceTime(300),
                    map(() => this.scrollContainer.nativeElement),
                    filter(
                        container =>
                            container.scrollTop + container.clientHeight >=
                            container.scrollHeight - 50,
                    ),
                    tap(() => this.#loadContacts(false)),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        }
    }

    #hasMoreData(): boolean {
        return (
            this.#contactDirectory().results.length <
            this.#contactDirectory().total
        );
    }

    #loadContacts(initialLoad: boolean) {
        if ((!initialLoad && !this.#hasMoreData()) || this.isLoading()) return;

        this.isLoading.set(true);

        return this.#contactDirectoryService
            .fetchContactDirectory(
                LIMIT,
                this.#contactDirectory().results.length,
                this.searchText().length > 0 ? this.searchText() : undefined,
                this.#directory(),
                this.#requiresName(),
                this.#requiresEmail(),
                this.#requiresPhone(),
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(data => {
                this.#contactDirectory.update(current => {
                    return {
                        total: data.total,
                        results: [...current.results, ...data.results],
                    };
                });

                this.isLoading.set(false);
            });
    }

    selectedChanged(event: IDirectoryListingEvent) {
        this.clicked.emit({
            type: event.type,
            item: this.#contactDirectory().results.find(
                c => c.id.toString() === event.item.id,
            ) as IContact,
        });
    }

    searchChanged(searchText: string) {
        this.#contactDirectory.set({
            total: 0,
            results: [],
        });

        this.searchText.set(searchText);
        this.#loadContacts(true);
    }

    refresh(contact: IContact) {
        this.#contactDirectory.update(current => {
            const index = current.results.findIndex(c => c.id === contact.id);

            if (index !== -1) {
                const updatedDirectory = [...current.results];
                updatedDirectory[index] = contact;

                return {
                    total: current.total,
                    results: updatedDirectory,
                };
            } else {
                return current;
            }
        });
    }

    get media(): MediaOptions {
        return this.#media();
    }

    set media(value: string) {
        this.#media.set(value as MediaOptions);

        switch (this.#media()) {
            case 'All': {
                this.#requiresEmail.set(false);
                this.#requiresPhone.set(false);
                break;
            }
            case 'Voice': {
                this.#requiresEmail.set(false);
                this.#requiresPhone.set(true);
                break;
            }
            case 'Email': {
                this.#requiresEmail.set(true);
                this.#requiresPhone.set(false);
                break;
            }
        }

        this.#contactDirectory.set({
            total: 0,
            results: [],
        });

        this.#loadContacts(true);
    }

    get directory(): number {
        return this.#directory();
    }

    set directory(value: number) {
        this.#contactDirectory.set({
            total: 0,
            results: [],
        });

        this.#directory.set(value);
        this.#loadContacts(true);
    }
}
